<template>
    <!--
      Cloudflare Turnstile CAPTCHA widget.

      Usage:
        <TurnstileWidget
          :site-key="siteKey"
          @token="onToken"
          @error="onError"
          @expired="onExpired"
        />

      Loads Turnstile's api.js once per page (idempotent). Emits `token`
      with the resolved Turnstile token on successful challenge. The
      server verifies this token against Cloudflare's siteverify before
      allowing the send-otp call.

      Instance callbacks are namespaced with a unique id so two widgets
      on the same page (or a remount during navigation) can't clobber
      each other's global callback handlers.
    -->
    <div class="turnstile-wrapper">
        <div v-if="!scriptFailed" ref="widgetEl" class="cf-turnstile"
             :data-sitekey="siteKey"
             :data-callback="cbNames.success"
             :data-error-callback="cbNames.error"
             :data-expired-callback="cbNames.expired"
             data-theme="light"
             data-size="normal"></div>
        <p v-if="errorMessage" class="text-caption text-error mt-2 text-center">
            {{ errorMessage }}
        </p>
        <button v-if="scriptFailed" type="button"
                class="text-caption text-primary mt-1 retry-link"
                @click="retry">
            Try again
        </button>
    </div>
</template>

<script setup>
import {onBeforeUnmount, onMounted, ref} from "vue";

const props = defineProps({
    siteKey: {type: String, required: true},
});
const emit = defineEmits(["token", "error", "expired"]);

const widgetEl = ref(null);
const errorMessage = ref("");
const scriptFailed = ref(false);
let widgetId = null;

// Per-instance callback names. Prevents collisions when the component
// is mounted twice on the same page (tests, nav + back, dev HMR) - the
// old instance's unmount handler no longer deletes the new instance's
// callbacks.
const instanceId = Math.random().toString(36).slice(2, 10);
const cbNames = {
    success: `__ts_cb_success_${instanceId}`,
    error: `__ts_cb_error_${instanceId}`,
    expired: `__ts_cb_expired_${instanceId}`,
};

const scriptSrc = "https://challenges.cloudflare.com/turnstile/v0/api.js";
// 8s timeout: challenges.cloudflare.com is usually <1s. If it doesn't
// land in 8s the user's network is blocking it (corporate firewall,
// offline, blocked by ad-blocker) and we should show a retry affordance
// instead of leaving the submit button permanently disabled.
const LOAD_TIMEOUT_MS = 8000;

function ensureScript() {
    return new Promise((resolve, reject) => {
        if (window.turnstile) return resolve();
        const existing = document.querySelector(`script[src="${scriptSrc}"]`);
        const timeout = setTimeout(
            () => reject(new Error("turnstile load timeout")),
            LOAD_TIMEOUT_MS,
        );
        const done = (err) => {
            clearTimeout(timeout);
            err ? reject(err) : resolve();
        };
        if (existing) {
            existing.addEventListener("load", () => done(), {once: true});
            existing.addEventListener(
                "error",
                () => done(new Error("turnstile load error")),
                {once: true},
            );
            return;
        }
        const s = document.createElement("script");
        s.src = scriptSrc;
        s.async = true;
        s.defer = true;
        s.onload = () => done();
        s.onerror = () => done(new Error("turnstile script failed to load"));
        document.head.appendChild(s);
    });
}

function render() {
    if (!widgetEl.value) return;
    if (!window.turnstile) return;
    try {
        widgetId = window.turnstile.render(widgetEl.value, {
            sitekey: props.siteKey,
            theme: "light",
            size: "normal",
            callback: (token) => emit("token", token),
            "error-callback": (code) => {
                errorMessage.value = "Verification failed. Please try again.";
                emit("error", code);
            },
            "expired-callback": () => {
                errorMessage.value = "Verification expired. Please try again.";
                emit("expired");
            },
        });
    } catch (e) {
        errorMessage.value = "Verification unavailable. Please refresh.";
        emit("error", String(e));
    }
}

async function loadAndRender() {
    errorMessage.value = "";
    scriptFailed.value = false;
    try {
        await ensureScript();
        render();
    } catch (e) {
        scriptFailed.value = true;
        errorMessage.value =
            "We can't reach the verification service. Check your network and try again.";
        emit("error", String(e));
    }
}

function retry() {
    loadAndRender();
}

onMounted(() => {
    // Install per-instance global callbacks so the data-attribute-rendered
    // widget (pre-script-load) still works if Turnstile renders before
    // our explicit render() call lands.
    window[cbNames.success] = (token) => emit("token", token);
    window[cbNames.error] = (code) => {
        errorMessage.value = "Verification failed. Please try again.";
        emit("error", code);
    };
    window[cbNames.expired] = () => {
        errorMessage.value = "Verification expired. Please try again.";
        emit("expired");
    };
    loadAndRender();
});

onBeforeUnmount(() => {
    // Best-effort cleanup - only delete OUR instance's callbacks. Other
    // widgets on the page (or the same widget remounted) keep theirs.
    try {
        if (widgetId !== null && window.turnstile) {
            window.turnstile.remove(widgetId);
        }
    } catch (_) {
        // ignore
    }
    delete window[cbNames.success];
    delete window[cbNames.error];
    delete window[cbNames.expired];
});
</script>

<style scoped>
.turnstile-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.retry-link {
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: underline;
}
</style>
